// C:\umkc_automation\frontend\src\components\Automation\StudentList\StudentExamsModal.tsx
import React, { useEffect, useState, useCallback } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import { CustomCheckbox } from '../../ui/CustomCheckbox';
import { useToast } from '../../../contexts/ToastContext';
import { formatDateTimeMsk } from '../../../utils/date';

// ─── Интерфейсы ───────────────────────────────────────────────────────────────

interface NestedProgram {
    id: number;
    name: string;
}

interface NestedStudentProgram {
    student_id: number;
    program_id: number;
    program: NestedProgram;
}

interface StudentForModal {
    id: number;
    full_name: string;
    student_programs: NestedStudentProgram[];
}

interface ApiAttempt {
    sdo_attempt_id: string;
    sdo_test_id: string;
    submitted_at: string;
    is_passed: boolean;
    score: number;
    report_path: string;
    id: number;
    download_url: string;
    correct_answers?: number;
    questions_count?: number;
}

interface AttemptWithProgram extends ApiAttempt {
    program_name: string;
    program_id: number;
}

interface StudentProgramResponse {
    student_id: number;
    program_id: number;
    status: string;
    start_date: string;
    end_date: string;
    exam_date: string;
    enrollment_date: string;
    attempts: ApiAttempt[];
    program: {
        name: string;
        duration_hours: number;
        is_active: boolean;
        external_url: string;
        platform_program_id: string;
        id: number;
        created_at: string;
        updated_at: string;
        department: {
            name: string;
            subfolder_name: string;
            id: number;
        };
    };
}

interface StudentProgram {
    name: string;
    duration_hours: number;
    is_active: boolean;
    external_url: string;
    platform_program_id: string;
    id: number;
    created_at: string;
    updated_at: string;
    department: {
        name: string;
        subfolder_name: string;
        id: number;
    };
}

interface StudentExamsModalProps {
    isOpen: boolean;
    onClose: () => void;
    student: StudentForModal | null;
    onLogout: () => void;
}

// ─── Компонент ────────────────────────────────────────────────────────────────

const StudentExamsModal: React.FC<StudentExamsModalProps> = ({
    isOpen,
    onClose,
    student,
    onLogout,
}) => {
    const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;
    const DOWNLOAD_HOST = process.env.REACT_APP_API_BASE_URL;

    const { addToast } = useToast();

    // ── Данные ──────────────────────────────────────────────────────────────
    const [attempts, setAttempts] = useState<AttemptWithProgram[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [lastUpdated, setLastUpdated] = useState<string>('');

    // ── Выбор и сброс попыток ────────────────────────────────────────────────
    const [selectedAttemptIds, setSelectedAttemptIds] = useState<Set<number>>(new Set());
    const [resetting, setResetting] = useState<boolean>(false);

    // ── Загрузка попыток ─────────────────────────────────────────────────────
    const fetchAttemptsWithPrograms = useCallback(async (studentId: number) => {
        const token = localStorage.getItem('token');
        if (!token) return;

        setLoading(true);
        try {
            const reportRes = await fetch(`${API_BASE_URL}/reporting/run-now`, {
                method: 'POST',
                headers: { Authorization: token },
            });

            if (reportRes.status === 401) {
                onLogout();
                return;
            }

            // Небольшая пауза, чтобы отчёт успел сформироваться на сервере
            await new Promise(resolve => setTimeout(resolve, 2000));

            const programsResponse = await fetch(
                `${API_BASE_URL}/students/${studentId}/programs`,
                { headers: { Authorization: token } }
            );

            if (programsResponse.status === 401) {
                onLogout();
                return;
            }

            if (!programsResponse.ok) {
                console.error('Error fetching programs:', programsResponse.status);
                addToast({ type: 'error', message: 'Не удалось загрузить программы студента.' });
                setLoading(false);
                return;
            }

            const programs: StudentProgram[] = await programsResponse.json();
            const allAttempts: AttemptWithProgram[] = [];

            await Promise.all(
                programs.map(async program => {
                    try {
                        const spResponse = await fetch(
                            `${API_BASE_URL}/student_programs/${studentId}/${program.id}`,
                            { headers: { Authorization: token } }
                        );

                        if (spResponse.status === 401) {
                            onLogout();
                            return;
                        }

                        if (spResponse.ok) {
                            const spData: StudentProgramResponse = await spResponse.json();
                            const programName = spData.program?.name || 'Неизвестная программа';

                            for (const attempt of spData.attempts || []) {
                                allAttempts.push({
                                    ...attempt,
                                    program_name: programName,
                                    program_id: program.id,
                                });
                            }
                        }
                    } catch (err) {
                        console.error(
                            `Error fetching student_program for program ${program.id}:`,
                            err
                        );
                    }
                })
            );

            allAttempts.sort(
                (a, b) =>
                    new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
            );

            setAttempts(allAttempts);
            setLastUpdated(
                new Date().toLocaleString('ru-RU', { timeZone: 'Europe/Moscow' })
            );
        } catch (error) {
            console.error('Sequence error:', error);
            addToast({ type: 'error', message: 'Произошла ошибка при загрузке данных.' });
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, onLogout, addToast]);

    // ── Сброс состояния при открытии/закрытии ────────────────────────────────
    useEffect(() => {
        if (isOpen && student) {
            fetchAttemptsWithPrograms(student.id);
        } else {
            setAttempts([]);
            setLastUpdated('');
            setSelectedAttemptIds(new Set());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, student]);

    // Сбрасываем выделение при каждом обновлении списка попыток
    useEffect(() => {
        setSelectedAttemptIds(new Set());
    }, [attempts]);

    // ── Выбор попыток ────────────────────────────────────────────────────────
    const handleToggleAttempt = (attemptId: number) => {
        setSelectedAttemptIds(prev => {
            const next = new Set(prev);
            if (next.has(attemptId)) {
                next.delete(attemptId);
            } else {
                next.add(attemptId);
            }
            return next;
        });
    };

    // ── Сброс выбранных попыток ──────────────────────────────────────────────
    const handleResetSelected = useCallback(async () => {
        if (selectedAttemptIds.size === 0 || !student) return;

        const token = localStorage.getItem('token');
        if (!token) return;

        const confirmed = window.confirm(
            `Вы уверены, что хотите сбросить ${selectedAttemptIds.size} попытку(и) ` +
            `для студента "${student.full_name}"?\n\n` +
            `Это действие удалит попытки на платформе SDO.`
        );
        if (!confirmed) return;

        setResetting(true);

        const ids = Array.from(selectedAttemptIds);
        let successCount = 0;
        let errorCount = 0;

        // Последовательный сброс — не перегружаем API
        for (const attemptId of ids) {
            try {
                const response = await fetch(
                    `${API_BASE_URL}/attempts/${attemptId}/reset`,
                    {
                        method: 'POST',
                        headers: {
                            Accept: 'application/json',
                            Authorization: token,
                        },
                    }
                );

                if (response.status === 401) {
                    onLogout();
                    return;
                }

                if (response.ok) {
                    successCount++;
                } else {
                    errorCount++;
                    const errorData = await response.json().catch(() => null);
                    console.error(
                        `Failed to reset attempt ${attemptId}:`,
                        response.status,
                        errorData
                    );
                }
            } catch (err) {
                errorCount++;
                console.error(`Error resetting attempt ${attemptId}:`, err);
            }
        }

        // Очищаем выбранные id перед обновлением
        setSelectedAttemptIds(new Set());

        // Сразу перезапрашиваем актуальный список с сервера
        await fetchAttemptsWithPrograms(student.id);

        setResetting(false);

        if (errorCount === 0) {
            addToast({
                type: 'success',
                message: `Успешно сброшено попыток: ${successCount}.`,
                duration: 5000,
            });
        } else if (successCount === 0) {
            addToast({
                type: 'error',
                message: `Не удалось сбросить попытки (${errorCount}). Проверьте подключение или обратитесь к администратору.`,
                duration: 6000,
            });
        } else {
            addToast({
                type: 'warning',
                message: `Сброшено: ${successCount}, ошибок: ${errorCount}. Некоторые попытки не удалось сбросить.`,
                duration: 6000,
            });
        }
    }, [selectedAttemptIds, student, API_BASE_URL, onLogout, addToast, fetchAttemptsWithPrograms]);

    // ── Скачивание протокола ─────────────────────────────────────────────────
    const handleDownloadProtocol = async (attemptId: number) => {
        const token = localStorage.getItem('token');
        const url = `${DOWNLOAD_HOST}/api/v1/attempts/${attemptId}/download`;

        try {
            const response = await fetch(url, {
                headers: { Authorization: token || '' },
            });

            if (response.status === 401) {
                onLogout();
                return;
            }

            if (!response.ok) {
                addToast({ type: 'error', message: 'Не удалось скачать файл протокола.' });
                return;
            }

            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);

            let fileName = `${student?.full_name || 'Студент'}.docx`;

            const contentDisposition = response.headers.get('Content-Disposition');
            if (contentDisposition) {
                const filenameStarMatch = contentDisposition.match(
                    /filename\*\s*=\s*(?:UTF-8|utf-8)'[^']*'(.+?)(?:;|$)/i
                );
                if (filenameStarMatch) {
                    try {
                        fileName = decodeURIComponent(filenameStarMatch[1].trim());
                    } catch (e) {
                        console.error('Error decoding filename*:', e);
                    }
                } else {
                    const filenameMatch = contentDisposition.match(
                        /filename\s*=\s*"?([^";\n]+)"?/i
                    );
                    if (filenameMatch) {
                        try {
                            fileName = decodeURIComponent(filenameMatch[1].trim());
                        } catch {
                            fileName = filenameMatch[1].trim();
                        }
                    }
                }
            }

            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Download error:', error);
            addToast({ type: 'error', message: 'Произошла ошибка при скачивании файла.' });
        }
    };

    // ── Утилиты ──────────────────────────────────────────────────────────────
    const formatScore = (attempt: AttemptWithProgram): string => {
        if (
            attempt.correct_answers !== undefined &&
            attempt.questions_count !== undefined
        ) {
            return `${attempt.correct_answers} / ${attempt.questions_count}`;
        }
        return `${attempt.score}`;
    };

    // ── Ранний выход ─────────────────────────────────────────────────────────
    if (!isOpen || !student) return null;

    // ── Рендер ───────────────────────────────────────────────────────────────
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label="Экзамены студента"
        >
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-[1px] transition-opacity"
                onClick={onClose}
            />

            {/* Модальное окно */}
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">

                {/* ── Header ── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <CheckCircleIcon className="text-blue-600" />
                        Экзамены студента
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition"
                        aria-label="Закрыть"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="p-6 overflow-y-auto flex-1">

                    {/* Имя студента + время обновления */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
                        <h3 className="text-lg font-bold text-gray-800">
                            {student.full_name}
                        </h3>
                        {lastUpdated && (
                            <span className="text-xs text-gray-400">
                                Обновлено: {lastUpdated}
                            </span>
                        )}
                    </div>

                    {/* Контент */}
                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                        </div>
                    ) : attempts.length === 0 ? (
                        <div className="text-center py-10 bg-gray-50 rounded border border-gray-100">
                            <p className="text-gray-500">
                                Данных о попытках сдачи экзаменов не найдено.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {/* Пустая ячейка вместо селекта "выбрать все" */}
                                        <th scope="col" className="px-4 py-3 w-10" />
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                                        >
                                            Программа
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                                        >
                                            Ответы
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                                        >
                                            Результат
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                                        >
                                            Дата
                                        </th>
                                        <th
                                            scope="col"
                                            className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider w-24"
                                        >
                                            Протокол
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {attempts.map(attempt => {
                                        const isSelected = selectedAttemptIds.has(attempt.id);

                                        // Подсветка строки по статусу
                                        const rowBg = isSelected ? 'bg-blue-50' : 'hover:bg-gray-50';

                                        return (
                                            <tr
                                                key={attempt.sdo_attempt_id}
                                                className={`transition-colors ${rowBg}`}
                                            >
                                                {/* Чекбокс */}
                                                <td className="px-4 py-4 w-10">
                                                    <div className="flex items-center justify-center">
                                                        <CustomCheckbox
                                                            id={`attempt-${attempt.id}`}
                                                            checked={isSelected}
                                                            onChange={() =>
                                                                handleToggleAttempt(attempt.id)
                                                            }
                                                            disabled={resetting}
                                                        />
                                                    </div>
                                                </td>

                                                {/* Программа */}
                                                <td className="px-4 py-4 text-sm text-gray-900 font-medium max-w-xs">
                                                    <span
                                                        className="line-clamp-2"
                                                        title={attempt.program_name}
                                                    >
                                                        {attempt.program_name}
                                                    </span>
                                                </td>

                                                {/* Ответы */}
                                                <td className="px-4 py-4 text-sm text-gray-700">
                                                    {formatScore(attempt)}
                                                </td>

                                                {/* Результат */}
                                                <td className="px-4 py-4 text-sm">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                            attempt.is_passed
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-red-100 text-red-800'
                                                        }`}
                                                    >
                                                        {attempt.is_passed ? 'Сдан' : 'Не сдан'}
                                                    </span>
                                                </td>

                                                {/* Дата */}
                                                <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">
                                                    {formatDateTimeMsk(attempt.submitted_at)}
                                                </td>

                                                {/* Скачать протокол */}
                                                <td className="px-4 py-4 text-center whitespace-nowrap">
                                                    <button
                                                        onClick={() =>
                                                            handleDownloadProtocol(attempt.id)
                                                        }
                                                        className="w-9 h-9 flex items-center justify-center mx-auto text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition"
                                                        title="Скачать протокол"
                                                    >
                                                        <FileDownloadIcon fontSize="small" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
                    <button
                        onClick={onClose}
                        className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
                    >
                        Закрыть
                    </button>

                    <button
                        onClick={handleResetSelected}
                        disabled={selectedAttemptIds.size === 0 || resetting}
                        className="flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {resetting ? 'Сброс...' : 'Удалить попытки'}
                    </button>

                    <button
                        onClick={() => fetchAttemptsWithPrograms(student.id)}
                        disabled={loading || resetting}
                        className="flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm transition disabled:opacity-70"
                    >
                        <AutorenewIcon
                            fontSize="small"
                            className={`mr-2 ${loading ? 'animate-spin' : ''}`}
                        />
                        Обновить
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StudentExamsModal;